import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { DataField, FormatType } from '../../types/viewspec';

interface TimeSeriesChartProps {
  title?: string;
  data: Record<string, unknown>[];
  xAxis: DataField;
  yAxis: DataField | DataField[];
  chartType: 'line' | 'area';
  showGrid?: boolean;
  showLegend?: boolean;
}

// Pentatonic brand colors for charts
const COLORS = ['#171717', '#00FBA9', '#3941EB', '#FF4C00', '#5BC48B', '#9EA8FF'];

function formatValue(value: unknown, format?: FormatType): string {
  if (value === null || value === undefined) return '';

  switch (format) {
    case 'number':
      return typeof value === 'number'
        ? value.toLocaleString()
        : String(value);
    case 'currency':
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
        : String(value);
    case 'percent':
      return typeof value === 'number'
        ? `${(value * 100).toFixed(1)}%`
        : String(value);
    case 'date':
      return value instanceof Date
        ? value.toLocaleDateString()
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value).toLocaleDateString()
        : String(value);
    case 'datetime':
      return value instanceof Date
        ? value.toLocaleString()
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value).toLocaleString()
        : String(value);
    default:
      return String(value);
  }
}

export default function TimeSeriesChart({
  title,
  data,
  xAxis,
  yAxis,
  chartType,
  showGrid = true,
  showLegend = true,
}: TimeSeriesChartProps) {
  const yAxes = Array.isArray(yAxis) ? yAxis : [yAxis];
  const ChartComponent = chartType === 'area' ? AreaChart : LineChart;

  return (
    <div className="bg-grey-light p-4 flex flex-col" style={{ minHeight: '300px' }}>
      {title && (
        <h3 className="text-h3 font-light text-black mb-4">
          {title}
        </h3>
      )}
      <div className="flex-1" style={{ minHeight: '250px' }}>
        <ResponsiveContainer width="100%" height={250}>
          <ChartComponent data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#A9A9A9" strokeOpacity={0.3} />}
            <XAxis
              dataKey={xAxis.key}
              tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
              tickFormatter={(v) => formatValue(v, xAxis.format)}
              axisLine={{ stroke: '#A9A9A9' }}
              tickLine={{ stroke: '#A9A9A9' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#A9A9A9', fontFamily: 'Atkinson Hyperlegible Next' }}
              tickFormatter={(v) => formatValue(v, yAxes[0]?.format)}
              axisLine={{ stroke: '#A9A9A9' }}
              tickLine={{ stroke: '#A9A9A9' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #A9A9A9',
                borderRadius: '0',
                boxShadow: 'none',
                fontFamily: 'Atkinson Hyperlegible Next',
              }}
              formatter={(value: number, name: string) => [
                formatValue(value, yAxes.find((y) => y.key === name)?.format),
                yAxes.find((y) => y.key === name)?.label || name,
              ]}
              labelFormatter={(label) => formatValue(label, xAxis.format)}
            />
            {showLegend && <Legend wrapperStyle={{ fontFamily: 'Atkinson Hyperlegible Next', fontSize: 12 }} />}
            {yAxes.map((axis, i) =>
              chartType === 'area' ? (
                <Area
                  key={axis.key}
                  type="monotone"
                  dataKey={axis.key}
                  name={axis.label}
                  stroke={axis.color || COLORS[i % COLORS.length]}
                  fill={axis.color || COLORS[i % COLORS.length]}
                  fillOpacity={0.15}
                  strokeWidth={1}
                />
              ) : (
                <Line
                  key={axis.key}
                  type="monotone"
                  dataKey={axis.key}
                  name={axis.label}
                  stroke={axis.color || COLORS[i % COLORS.length]}
                  strokeWidth={1}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              )
            )}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
